'use client'

/**
 * /budget — v3 surface (Bill prototype 2026-05-30 → app.go-atrium.com).
 *
 * Layout:
 *   • .bv3-eyebrow             — "→ Budget" mono label + meta on the right
 *   • .bv3-mphero              — centered "Put every dollar where it works."
 *   • .bv3-stats               — live MTD ROAS / CPA / Conversions
 *   • .bv3-alhead + .bv3-alnote — section header + smart-allocation toggle
 *   • .bv3-pband               — projected blended ROAS + deployed vs limit
 *   • .bv3-arow × N            — per-channel allocation rows (subchannel
 *                                  detail preserved as a sub-list inside)
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { getBudgetExampleState } from '@/lib/budget/fallback-data'
import { SmartSpendWorkspaceToggle } from '@/components/smart-spend/SmartSpendWorkspaceToggle'
import {
  ChannelAllocationCard,
  type ChannelView,
} from '@/components/budget/BudgetAllocation'
import type { BudgetStatsResponse } from '@/app/api/budget/stats/route'
import type {
  ChannelMetricsResponse,
  ChannelMetricsItem,
  SubchannelMetricsItem,
} from '@/app/api/connectors/catalog/metrics/route'
import { LobbyHeaderMeta } from '@/contexts/LobbyHeaderMetaContext'

const SHOW_SMART_SPEND_WORKSPACE_TOGGLE = false

// ── Page ────────────────────────────────────────────────────────────────────

export default function BudgetPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { workspaceId, isLoggedIn, isLoading: workspaceLoading } = useWorkspace()

  const [channels, setChannels] = useState<ChannelView[]>([])
  const [channelMetrics, setChannelMetrics] = useState<Record<string, ChannelMetricsItem>>({})
  const [subchannelMetrics, setSubchannelMetrics] = useState<Record<string, SubchannelMetricsItem>>({})
  const [workspaceSmartSpendEnabled, setWorkspaceSmartSpendEnabled] = useState(false)
  const [monthlyCapCents, setMonthlyCapCents] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    if (!isLoggedIn) {
      const example = getBudgetExampleState()
      setChannels(example.channels)
      setChannelMetrics(example.channelMetrics)
      setSubchannelMetrics(example.subchannelMetrics)
      setMonthlyCapCents(example.monthlyCapCents)
      setLoading(false)
      return
    }
    try {
      const [cRes, heroRes, metricsRes] = await Promise.all([
        fetch('/api/connectors/catalog'),
        fetch('/api/budget/hero'),
        fetch('/api/connectors/catalog/metrics'),
      ])
      if (cRes.ok) {
        const cData = (await cRes.json()) as {
          channels?: ChannelView[]
          workspace_id?: string
          workspace_smart_spend_enabled?: boolean
        }
        setChannels(cData.channels ?? [])
        setWorkspaceSmartSpendEnabled(false)
      }
      if (heroRes.ok) {
        const heroData = (await heroRes.json()) as {
          monthly_cap_cents?: number | null
        }
        setMonthlyCapCents(heroData.monthly_cap_cents ?? null)
      }
      if (metricsRes.ok) {
        const metricsData = (await metricsRes.json()) as ChannelMetricsResponse
        const byKey: Record<string, ChannelMetricsItem> = {}
        for (const row of metricsData.by_channel ?? []) byKey[row.channel_key] = row
        setChannelMetrics(byKey)

        const bySub: Record<string, SubchannelMetricsItem> = {}
        for (const row of metricsData.by_subchannel ?? []) bySub[row.subchannel_key] = row
        setSubchannelMetrics(bySub)
      }
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [isLoggedIn])

  useEffect(() => {
    if (workspaceLoading) return
    void reload()
  }, [reload, workspaceLoading])

  // Legacy wallet callback URLs still land on /budget from existing backend
  // routes. Forward them to /wallet without rendering billing UI here.
  useEffect(() => {
    const deposit = searchParams.get('deposit')
    const paymentMethod = searchParams.get('payment_method')

    if (deposit || paymentMethod) {
      router.replace(`/wallet?${searchParams.toString()}`)
    }
  }, [router, searchParams])

  // Aggregate totals + channels-live count for the eyebrow + .bv3-pband band.
  const totalDeployedCents = useMemo(
    () =>
      channels
        .flatMap((c) => c.subchannels)
        .filter(
          (s) => s.connection?.status === 'connected' || s.connection?.status === 'pending',
        )
        .reduce((acc, s) => acc + (s.connection?.monthly_budget_cents ?? 0), 0),
    [channels],
  )
  const channelsLive = useMemo(
    () =>
      channels.filter((c) =>
        c.subchannels.some(
          (s) => s.connection?.status === 'connected' || s.connection?.status === 'pending',
        ),
      ).length,
    [channels],
  )
  // Spend-weighted blended ROAS across channels that report a number.
  const blendedRoas = useMemo(() => {
    let weighted = 0
    let weight = 0
    for (const c of channels) {
      const m = channelMetrics[c.key]
      const channelSpend = c.subchannels.reduce(
        (acc, s) =>
          s.connection?.status === 'connected' || s.connection?.status === 'pending'
            ? acc + (s.connection?.monthly_budget_cents ?? 0)
            : acc,
        0,
      )
      if (m?.roas != null && channelSpend > 0) {
        weighted += m.roas * channelSpend
        weight += channelSpend
      }
    }
    return weight > 0 ? weighted / weight : null
  }, [channels, channelMetrics])

  return (
    <div className="bv3-canvas min-h-full">
      <LobbyHeaderMeta>
        {formatCents(totalDeployedCents)} / mo deployed · {channelsLive} channel
        {channelsLive === 1 ? '' : 's'} live
      </LobbyHeaderMeta>

      <div className="bv3-bd pt-6">
        <div className="bv3-bdwrap">
          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Centered hero header */}
          <div className="bv3-mphero">
            <h1>
              Put every dollar
              <br />
              <span className="grn">where it works.</span>
            </h1>
            <p className="sub">
              Decide how your monthly budget splits across channels and subchannels.
            </p>
          </div>

          {/* MTD live stats — kept from previous /budget; not in prototype. */}
          <BudgetStatsRow />

          {/* Budget allocation section */}
          <AllocationSection
            channels={channels}
            channelMetrics={channelMetrics}
            subchannelMetrics={subchannelMetrics}
            loading={loading && channels.length === 0}
            onReload={reload}
            workspaceId={workspaceId}
            workspaceSmartSpendEnabled={false}
            monthlyCapCents={monthlyCapCents}
            totalDeployedCents={totalDeployedCents}
            blendedRoas={blendedRoas}
            onSmartSpendChange={(_enabled) => {
              // Stephen 2026-06-01 (PR #714): Smart-Spend workspace toggle
              // hidden in v3 surface. Force-disable until further notice.
              setWorkspaceSmartSpendEnabled(false)
              void reload()
            }}
          />
        </div>
      </div>
    </div>
  )
}

// ── MTD stats row (live data the prototype doesn't surface) ─────────────────

function BudgetStatsRow() {
  const [stats, setStats] = useState<BudgetStatsResponse | null>(null)

  useEffect(() => {
    void fetch('/api/budget/stats')
      .then((r) => (r.ok ? r.json() : null))
      .then((d: BudgetStatsResponse | null) => {
        if (d) setStats(d)
      })
      .catch(() => {})
  }, [])

  if (!stats) return null
  const hasData = stats.roas !== null || stats.cpa_cents !== null || stats.conversions > 0
  if (!hasData) return null

  return (
    <div className="bv3-stats">
      <span>
        <span className="k">ROAS MTD</span>
        <span className="v">{stats.roas != null ? `${stats.roas.toFixed(2)}×` : '—'}</span>
      </span>
      <span className="sep">·</span>
      <span>
        <span className="k">CPA</span>
        <span className="v">
          {stats.cpa_cents != null
            ? (stats.cpa_cents / 100).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 2,
              })
            : '—'}
        </span>
      </span>
      <span className="sep">·</span>
      <span>
        <span className="k">Conversions</span>
        <span className="v">{stats.conversions.toLocaleString()}</span>
      </span>
    </div>
  )
}

// ── Allocation section ──────────────────────────────────────────────────────

function AllocationSection({
  channels,
  channelMetrics,
  subchannelMetrics,
  loading,
  onReload,
  workspaceId,
  workspaceSmartSpendEnabled,
  monthlyCapCents,
  totalDeployedCents,
  blendedRoas,
  onSmartSpendChange,
}: {
  channels: ChannelView[]
  channelMetrics: Record<string, ChannelMetricsItem>
  subchannelMetrics: Record<string, SubchannelMetricsItem>
  loading: boolean
  onReload: () => void | Promise<void>
  workspaceId: string | null
  workspaceSmartSpendEnabled: boolean
  monthlyCapCents: number | null
  totalDeployedCents: number
  blendedRoas: number | null
  onSmartSpendChange: (enabled: boolean) => void
}) {
  const totalAllocatedCents = useMemo(
    () =>
      channels
        .flatMap((c) => c.subchannels)
        .filter(
          (s) =>
            s.connection?.status === 'connected' || s.connection?.status === 'pending',
        )
        .reduce((acc, s) => acc + (s.connection?.monthly_budget_cents ?? 0), 0),
    [channels],
  )
  const totalManagedCents = useMemo(
    () =>
      channels
        .flatMap((c) => c.subchannels)
        .filter(
          (s) =>
            (s.connection?.status === 'connected' || s.connection?.status === 'pending') &&
            s.connection.auth_mode === 'atrium_managed',
        )
        .reduce((acc, s) => acc + (s.connection?.monthly_budget_cents ?? 0), 0),
    [channels],
  )

  const initialLiveBudgetByConnection = useMemo(() => {
    const next: Record<string, number> = {}
    for (const channel of channels) {
      for (const subchannel of channel.subchannels) {
        const connection = subchannel.connection
        if (
          connection &&
          (connection.status === 'connected' || connection.status === 'pending')
        ) {
          next[connection.id] = connection.monthly_budget_cents
        }
      }
    }
    return next
  }, [channels])
  const [liveBudgetByConnection, setLiveBudgetByConnection] = useState<Record<string, number>>({})

  useEffect(() => {
    setLiveBudgetByConnection(initialLiveBudgetByConnection)
  }, [initialLiveBudgetByConnection])

  const handleLiveBudgetChange = useCallback((connectionId: string, cents: number) => {
    setLiveBudgetByConnection((prev) => ({ ...prev, [connectionId]: cents }))
  }, [])

  const liveAllocatedCents = useMemo(
    () => Object.values(liveBudgetByConnection).reduce((acc, cents) => acc + cents, 0),
    [liveBudgetByConnection],
  )

  const sortedChannels = useMemo(
    () => [...channels].sort((a, b) => a.display_order - b.display_order),
    [channels],
  )

  const deployedPct =
    monthlyCapCents != null && monthlyCapCents > 0
      ? Math.min(100, (liveAllocatedCents / monthlyCapCents) * 100)
      : 0
  const overCap = monthlyCapCents != null && liveAllocatedCents > monthlyCapCents

  return (
    <section>
      {/* Section header — title + dep meta on left, Smart-allocation on right.
          Always rendered so the structural anchor sits below the hero stack
          even while data is fetching or empty.
          Stephen 2026-06-01 (PR #714): Smart-Spend workspace toggle is gated
          out of UI via SHOW_SMART_SPEND_WORKSPACE_TOGGLE while we rethink
          the UX before production launch. */}
      <div className="bv3-alhead">
        <div>
          <span className="t">Budget allocation</span>
          <span className="dep">{formatCents(totalDeployedCents)} / mo deployed</span>
        </div>
        {SHOW_SMART_SPEND_WORKSPACE_TOGGLE && workspaceId ? (
          <div id="smart-spend-workspace-toggle">
            <SmartSpendWorkspaceToggle
              variant="inline"
              enabled={workspaceSmartSpendEnabled && false}
              workspaceId={workspaceId}
              onChange={onSmartSpendChange}
            />
          </div>
        ) : null}
      </div>
      <div className="bv3-alnote">
        {workspaceSmartSpendEnabled
          ? 'Atrium is balancing spend toward your best-ROAS channels.'
          : 'Drag to reallocate spend. Atrium projects the ROAS impact live.'}
      </div>

      {/* Projection band */}
      <div className="bv3-pband">
        <div>
          <div className="pl">Projected blended ROAS</div>
          <div className="pv">{blendedRoas != null ? `${blendedRoas.toFixed(2)}×` : '—'}</div>
        </div>
        <div className="pbprog">
          <div className="pl">
            {monthlyCapCents != null ? 'Deployed vs monthly limit' : 'Total deployed'}
          </div>
          <div className={`pp${overCap ? ' over' : ''}`}>
            <div style={{ width: `${deployedPct}%` }} />
          </div>
        </div>
        <div>
          <div className="pl">Total deployed</div>
          <div className="pv tabular-nums">{formatCents(liveAllocatedCents)}</div>
        </div>
      </div>

      {/* Per-channel allocation rows */}
      {loading && sortedChannels.length === 0 ? (
        <AllocationRowSkeleton count={4} />
      ) : sortedChannels.length === 0 ? (
        <AllocationEmpty />
      ) : (
        <div>
          {sortedChannels.map((ch) => (
            <div
              key={ch.key}
              id={`channel-group-${ch.key}`}
              className="transition-all duration-300"
            >
              <ChannelAllocationCard
                channel={ch}
                metrics={channelMetrics[ch.key] ?? null}
                subchannelMetrics={subchannelMetrics}
                onReload={onReload}
                workspaceSmartSpendEnabled={workspaceSmartSpendEnabled}
                monthlyCapCents={monthlyCapCents}
                totalAllocatedCents={totalAllocatedCents}
                totalManagedCents={totalManagedCents}
                liveBudgetByConnection={liveBudgetByConnection}
                onLiveBudgetChange={handleLiveBudgetChange}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function AllocationRowSkeleton({ count }: { count: number }) {
  return (
    <div>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="mb-3.5 animate-pulse rounded-card border-[0.5px] border-line bg-surface-1 px-[22px] py-[18px]"
        >
          <div className="flex items-center gap-4">
            <div className="size-11 rounded-xl bg-line" />
            <div className="h-4 flex-1 max-w-[180px] rounded bg-line" />
            <div className="h-4 w-20 rounded bg-line" />
          </div>
          <div className="mt-3 h-3 w-48 rounded bg-line/70" />
        </div>
      ))}
    </div>
  )
}

function AllocationEmpty() {
  return (
    <div
      className="bv3-arow"
      style={{
        ['--c' as string]: 'var(--color-line-2)',
        textAlign: 'center',
        padding: '24px 22px',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', marginBottom: 4 }}>
        No channels connected yet
      </div>
      <div style={{ fontSize: 13, color: '#888' }}>
        Connect channels in the{' '}
        <Link
          href="/marketplace"
          style={{ color: 'var(--color-brand)', fontWeight: 600, textDecoration: 'underline' }}
        >
          marketplace
        </Link>{' '}
        to start allocating budget.
      </div>
    </div>
  )
}

// ── Utilities ────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  if (cents === 0) return '$0'
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}
